import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";

import {useSessionStore} from "@/src/modules/auth/store/session";
import {captureRealtimeConnectionFailed} from "@/src/modules/core/lib/glitchtip/device-capture";
import {inferRealtimeFailurePhase, type RealtimeConnectTrigger} from "@/src/modules/core/lib/glitchtip/diagnostics";
import type {HttpContext} from "@/src/modules/core/lib/glitchtip/http-context";

import {fetchBroadcastingConfig, openAuthenticatedPusherWebSocket} from "../lib/broadcasting";
import {useMeStore} from "../store/me";

const SOCKET_PING_INTERVAL = 25000;
const MAX_RECONNECTION_TRIES = 5;

const PING_MESSAGE = {
  event: "pusher:ping",
};

const BROADCASTING_CONFIG_HTTP: HttpContext = {
  httpMethod: "GET",
  httpPath: "broadcasting/config",
  httpStatus: "0",
};

type SocketContextType = {
  isSocketConnected: boolean;
  socket: WebSocket | null;
  setSocket: (socket: WebSocket | null) => void;
};
const SocketContext = createContext<SocketContextType>({
  isSocketConnected: false,
  socket: null,
  setSocket: () => {},
});
SocketContext.displayName = "SocketContext";

function reportRealtimeFailure(
  error: unknown,
  phase?: ReturnType<typeof inferRealtimeFailurePhase>,
  context?: {
    axiosError?: import("axios").AxiosError;
    connectTrigger?: RealtimeConnectTrigger;
    configFetchAttempts?: number;
    configFetchDurationMs?: number;
    reconnectAttempt?: number;
  },
) {
  if (import.meta.env.DEV) return;

  captureRealtimeConnectionFailed({
    http: BROADCASTING_CONFIG_HTTP,
    phase: phase ?? inferRealtimeFailurePhase(error),
    error,
    axiosError: context?.axiosError,
    connectTrigger: context?.connectTrigger,
    configFetchAttempts: context?.configFetchAttempts,
    configFetchDurationMs: context?.configFetchDurationMs,
    reconnectAttempt: context?.reconnectAttempt,
  });
}

function SocketProvider({children}: {children: React.ReactNode}) {
  const [socket, setSocket] = useState<WebSocket | null>(null);

  const context = useMemo(
    () => ({
      isSocketConnected: socket?.readyState === WebSocket.OPEN,
      socket,
      setSocket,
    }),
    [socket, setSocket],
  );
  return <SocketContext.Provider value={context}>{children}</SocketContext.Provider>;
}

function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
}

function useConnect() {
  const {setSocket} = useSocket();
  const connectTriggerRef = useRef<RealtimeConnectTrigger>("initial");

  const connect = useCallback(async () => {
    const connectTrigger = connectTriggerRef.current;
    connectTriggerRef.current = "reconnect";

    try {
      const me = useMeStore.getState().me;
      const token = useSessionStore.getState().session?.access_token;
      if (!me || !token) return;

      const configFetchStartedAt = performance.now();
      const fetchResult = await fetchBroadcastingConfig();
      const configFetchDurationMs = Math.round(performance.now() - configFetchStartedAt);
      if (!fetchResult.config) {
        reportRealtimeFailure(
          fetchResult.lastError ?? new Error("broadcasting config unavailable"),
          "config_fetch",
          {
            axiosError: fetchResult.lastAxiosError,
            connectTrigger,
            configFetchAttempts: fetchResult.attempts,
            configFetchDurationMs,
          },
        );
        return;
      }

      const channelName = `private-user.${me.user_id}`;
      try {
        const ws = await openAuthenticatedPusherWebSocket(fetchResult.config, channelName);
        setSocket(ws);
      } catch (error) {
        console.error("Failed to create WebSocket connection:", error);
        reportRealtimeFailure(error, undefined, {connectTrigger});
      }
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      reportRealtimeFailure(error, "config_fetch", {connectTrigger});
    }
  }, [setSocket]);

  const markUserChangeConnect = useCallback(() => {
    connectTriggerRef.current = "user_change";
  }, []);

  return {connect, markUserChangeConnect};
}

function usePingPong() {
  const {socket, setSocket} = useSocket();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPing = useCallback(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    intervalRef.current = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(PING_MESSAGE));
      }
    }, SOCKET_PING_INTERVAL);
  }, [socket]);

  const stopPing = useCallback(() => {
    intervalRef.current && clearInterval(intervalRef.current);
    intervalRef.current = null;

    socket && socket.close();
    setSocket(null);
  }, [socket, setSocket]);

  return {startPing, stopPing};
}

function useErrorHandler({stopPing}: {stopPing: () => void}) {
  const {socket} = useSocket();
  const {connect} = useConnect();
  const triesRef = useRef(0);

  const setErrorHandler = useCallback(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    triesRef.current = 0;
    socket.onclose = () => {
      stopPing();
      if (triesRef.current < MAX_RECONNECTION_TRIES) {
        triesRef.current += 1;
        setTimeout(connect, 5000 * triesRef.current);
        return;
      }

      reportRealtimeFailure(
        new Error("realtime reconnect exhausted"),
        "reconnect_exhausted",
        {connectTrigger: "reconnect", reconnectAttempt: triesRef.current},
      );
    };
  }, [socket, connect, stopPing]);

  return setErrorHandler;
}

function useSocketConnect({enabled = false}: {enabled?: boolean}) {
  const {connect, markUserChangeConnect} = useConnect();
  const {socket, setSocket} = useSocket();
  const {startPing, stopPing} = usePingPong();
  const setErrorHandler = useErrorHandler({stopPing});
  const meUserId = useMeStore((state) => state.me?.user_id);
  const previousUserIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return;

    stopPing();
    if (socket) {
      socket.close();
      setSocket(null);
    }

    if (
      previousUserIdRef.current !== undefined &&
      previousUserIdRef.current !== meUserId
    ) {
      markUserChangeConnect();
    }
    previousUserIdRef.current = meUserId;

    void connect();
  }, [enabled, meUserId]);

  useEffect(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    setErrorHandler();
    startPing();

    return () => {
      stopPing();
      socket.close();
    };
  }, [socket?.readyState]);
}

export {SocketProvider, useSocketConnect, useSocket};
